import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext.jsx';
import api from '../services/api.js';
import { ROLES } from '../utils/roles.js';
import { PAGE_REGISTRY } from '../utils/pageRoutes.js';
import { DEFAULT_ROUTE } from '../utils/menuConfig.js';
import HelpDrawer from './HelpDrawer/HelpDrawer.jsx';

// Route shortcuts
const R = {
  dashboard: '/dashboard',
  pullOrder: PAGE_REGISTRY[72447].route,
  demandDash: PAGE_REGISTRY[72449].route,
  production: PAGE_REGISTRY[72448].route,
  stockLedger: PAGE_REGISTRY[72450].route,
  transfers: PAGE_REGISTRY[72452].route,
  alerts: PAGE_REGISTRY[72453].route,
  recipes: PAGE_REGISTRY[72451].route,
  costReport: PAGE_REGISTRY[72455].route,
  systemSetup: PAGE_REGISTRY[72454].route,
  activityLog: PAGE_REGISTRY[72456].route,
};

// Role-based menu definitions. Each entry: { label, route, icon }.
// Groups are { label, icon, children: [...] }.
function buildMenu(hasRole) {
  const SA = ROLES.SUPER_ADMIN;
  const FI = ROLES.FINANCE;
  const BM = ROLES.BRAND_MANAGER;
  const CS = ROLES.COMMISSARY_SUPERVISOR;
  const OM = ROLES.OUTLET_MANAGER;
  const PS = ROLES.PROCUREMENT_STAFF;

  const items = [];

  // ── Orders ──────────────────────────────────────────
  if (hasRole(SA, FI, BM, CS, OM, PS)) {
    items.push({
      label: 'Order Desk',
      icon: 'fa fa-cart-flatbed',
      route: R.pullOrder,
    });
  }

  // ── Demand ──────────────────────────────────────────
  if (hasRole(SA, FI, BM, CS)) {
    items.push({
      label: 'Demand Dashboard',
      icon: 'fa fa-chart-line',
      route: R.demandDash,
    });
  }

  // ── Production ──────────────────────────────────────
  if (hasRole(SA, CS)) {
    items.push({
      label: 'Production',
      icon: 'fa fa-calendar-check',
      route: R.production,
    });
  }

  // ── Inventory & Transfers ────────────────────────────
  if (hasRole(SA, CS, PS)) {
    items.push({
      label: 'Inventory',
      icon: 'fa-solid fa-warehouse',
      badgeKey: 'alertCount',
      children: [
        { label: 'Stock Ledger', icon: 'fa-solid fa-table-list', route: R.stockLedger },
        { label: 'Inventory Alerts', icon: 'fa-solid fa-bell', route: R.alerts },
      ],
    });
  }

  if (hasRole(SA, CS, OM)) {
    items.push({
      label: 'Transfers',
      icon: 'fa fa-truck',
      route: R.transfers,
    });
  }

  // ── Finance & Admin ──────────────────────────────────
  const adminItems = [];

  if (hasRole(SA, FI)) {
    adminItems.push({ label: 'Recipes & BOM', icon: 'fa fa-flask', route: R.recipes });
    adminItems.push({ label: 'Cost Allocation', icon: 'fa fa-chart-bar', route: R.costReport });
  }

  if (hasRole(SA)) {
    adminItems.push({ label: 'System Setup', icon: 'fa fa-cog', route: R.systemSetup });
  }

  if (hasRole(SA, FI)) {
    adminItems.push({ label: 'Activity Log', icon: 'fa fa-clipboard-list', route: R.activityLog });
  }

  if (adminItems.length > 0) {
    items.push({ label: 'Admin', icon: 'fa fa-shield-halved', children: adminItems });
  }

  return items;
}

function NavItem({ item, level = 0, badges = {} }) {
  const hasChildren = item.children && item.children.length > 0;
  const linkCls = level === 0 ? 'nav-link text-white' : 'dropdown-item';
  const badgeVal = item.badgeKey ? badges[item.badgeKey] : null;

  if (!hasChildren) {
    return (
      <li className={level === 0 ? 'nav-item' : ''}>
        <NavLink
          to={item.route || '#'}
          className={({ isActive }) => `${linkCls}${isActive ? ' active' : ''}`}
          data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {item.icon && <i className={`${item.icon} mr-2`} />}
          {item.label}
        </NavLink>
      </li>
    );
  }

  const id = `mainMenu-${item.label.replace(/\W+/g, '_')}`;
  return (
    <li className="nav-item dropdown">
      <a
        href="#"
        role="button"
        id={id}
        className={`${level === 0 ? 'nav-link text-white' : 'dropdown-item'} dropdown-toggle`}
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
        onClick={(e) => e.preventDefault()}
      >
        {item.icon && <i className={`${item.icon} mr-2`} />}
        {item.label}
        {badgeVal > 0 && (
          <span className="badge badge-danger ml-2" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
            {badgeVal}
          </span>
        )}
      </a>
      <ul className="dropdown-menu dropdown-menu-dark" aria-labelledby={id} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {item.children.map((child) => (
          <NavItem key={child.route || child.label} item={child} level={level + 1} badges={badges} />
        ))}
      </ul>
    </li>
  );
}

export default function Navigation() {
  const { isAuthenticated, user, hasRole, logout } = useAuth();
  const [helpOpen, setHelpOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get('/stock-ledger/alerts').then((r) => setAlertCount((r.data.data || []).length)).catch(() => {});
  }, [isAuthenticated]);

  const menuItems = isAuthenticated ? buildMenu(hasRole) : [];
  const badges = { alertCount };

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary text-light fixed-top">
        <div className="container-fluid">
          <Link className="navbar-brand" to={isAuthenticated ? DEFAULT_ROUTE : '/login'}>
            <i className="fa-solid fa-utensils mr-2" />
            KitchenOS
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            data-toggle="collapse"
            data-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon" />
          </button>

          <div className="collapse navbar-collapse" id="navbarNav">
            {isAuthenticated && (
              <ul className="navbar-nav" data-testid="primary-nav">
                {menuItems.map((item) => (
                  <NavItem key={item.route || item.label} item={item} level={0} badges={badges} />
                ))}
              </ul>
            )}
          </div>

          <div className="ml-auto d-flex align-items-center" id="logged-in-user-info">
            {isAuthenticated && (
              <>
                {/* Help button */}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light mr-2"
                  onClick={() => setHelpOpen(true)}
                  aria-label="Open help documentation"
                  data-testid="nav-help"
                  title="Help & Documentation"
                >
                  <i className="fa-solid fa-circle-question mr-1" />
                  Help
                </button>

                <span className="text-light mr-3 small d-none d-md-inline" data-testid="logged-in-user-email">
                  <i className="fa-solid fa-user-circle mr-1" />
                  {user?.email}
                </span>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-light"
                  onClick={logout}
                  data-testid="logout-button"
                >
                  <i className="fa-solid fa-right-from-bracket mr-1" />
                  Logout
                </button>
              </>
            )}
            {!isAuthenticated && (
              <Link className="btn btn-sm btn-outline-light" to="/login" data-testid="nav-login">
                Sign in
              </Link>
            )}
          </div>
        </div>
      </nav>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}
